import React from 'react';
import { ArrowRight, Phone, Shield, FileText } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { companyInfo } from '../mock';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';

const Hero = () => {
  const { language } = useLanguage();

  const titles = {
    en: "Your peace of mind starts with the right guidance.",
    es: "Tu tranquilidad comienza con una buena orientación."
  };

  const subtitles = {
    en: "Protect your car, home, family, and business with Palerm Insurance Agency.",
    es: "Protege tu auto, hogar, familia y negocio con Palerm Insurance Agency."
  };

  return (
    <section className="relative min-h-[650px] lg:min-h-[700px] flex items-center overflow-hidden bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-100">
      {/* Decorative background shapes */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-400/20 to-cyan-400/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-cyan-400/20 to-blue-400/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-gradient-to-br from-blue-300/10 to-cyan-300/10 rounded-full blur-2xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Content - Text */}
          <div className="space-y-6 py-12 text-center lg:text-left">
            <div className="inline-block">
              <span className="bg-blue-600/10 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold border border-blue-600/20">
                {language === 'en' ? '25+ Years of Excellence' : '25+ Años de Excelencia'}
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-gray-900">
              {titles[language]}
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
              {subtitles[language]}
            </p>

            <div className="flex flex-wrap gap-4 pt-4 justify-center lg:justify-start">
              <Link 
                to="/quote/auto"
                className="inline-flex items-center bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-8 py-4 rounded-full text-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 group focus:outline-none focus:ring-4 focus:ring-red-300"
                aria-label={language === 'en' ? 'Get a free auto insurance quote' : 'Obtener cotización de seguro de auto gratis'}
              >
                <FileText className="w-5 h-5 mr-2" />
                {language === 'en' ? 'Get a Quote' : 'Obtener Cotización'}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              
              <a
                href={`tel:${companyInfo.phone}`}
                className="inline-flex items-center border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-blue-300"
                aria-label={language === 'en' ? `Call ${companyInfo.phone}` : `Llamar ${companyInfo.phone}`}
              >
                <Phone className="mr-2 w-5 h-5" />
                {companyInfo.phone}
              </a>
            </div>
          </div>

          {/* Right Content - Segurito */}
          <div className="flex flex-col items-center lg:items-end justify-center gap-6">
            <div className="relative w-full max-w-md lg:max-w-lg">
              {/* Decorative circle behind Segurito */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400/20 to-cyan-400/20 rounded-full blur-2xl scale-110"></div>
              
              {/* Segurito Image */}
              <div className="relative z-10 transform hover:scale-105 transition-transform duration-300">
                <img
                  src="/images/segurito-avatar.png"
                  alt="Segurito - Mascota de Palerm Insurance Agency"
                  className="w-full h-auto drop-shadow-2xl"
                  onError={(e) => {
                    console.error('Segurito image failed to load');
                  }}
                />
              </div>
            </div>

            {/* Experience badge - Below Segurito in normal flow */}
            <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-5 border-2 border-blue-100">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <Shield className="w-7 h-7 text-white" />
                </div>
                <div className="text-left flex-1 min-w-0">
                  <p className="text-base md:text-lg font-bold text-gray-900 leading-tight">
                    {language === 'en' ? 'Over 25 years of insurance experience' : 'Más de 25 años de experiencia en seguros'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;